import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { Shield, UserCheck, ShieldCheck, Loader2 } from "lucide-react";
import { UserProfile, UserRole } from "./types";
import { supabase } from "@/integrations/supabase/client";

interface UserCardProps {
  user: UserProfile;
  onUpdateRole: (userId: string, role: UserRole) => void;
}

export const UserCard = ({ user, onUpdateRole }: UserCardProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchEmail = async () => {
      try {
        const { data, error } = await supabase
          .rpc('get_user_email', { user_id: user.id });
        
        if (error) throw error;
        setEmail(data);
      } catch (error) {
        console.error('Error fetching user email:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmail();
  }, [user.id]);

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Shield className="h-4 w-4" />;
      case 'moderator':
        return <ShieldCheck className="h-4 w-4" />;
      default:
        return <UserCheck className="h-4 w-4" />;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'مدير';
      case 'moderator':
        return 'مشرف';
      default:
        return 'مستخدم';
    }
  };

  return (
    <Card className="transition-all duration-200 hover:shadow-md">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-3">
          <Avatar>
            <AvatarImage src={user.avatar_url || undefined} />
            <AvatarFallback>
              {email ? email.charAt(0).toUpperCase() : '?'}
            </AvatarFallback>
          </Avatar>
          <CardTitle className="text-xl">
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              email || 'مستخدم غير معروف'
            )}
          </CardTitle>
        </div>
        {isUpdating ? (
          <Select
            defaultValue={user.user_roles?.role || 'user'}
            onValueChange={(value: UserRole) => {
              onUpdateRole(user.id, value);
              setIsUpdating(false);
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="user" className="flex items-center gap-2">
                <UserCheck className="h-4 w-4" />
                مستخدم
              </SelectItem>
              <SelectItem value="moderator" className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" />
                مشرف
              </SelectItem>
              <SelectItem value="admin" className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                مدير
              </SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => setIsUpdating(true)}
          >
            {getRoleIcon(user.user_roles?.role || 'user')}
            {getRoleLabel(user.user_roles?.role || 'user')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-2">
          <p className="text-sm text-muted-foreground">
            تاريخ الانضمام: {new Date(user.created_at).toLocaleDateString()}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};